import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import CustomizedSwitch from '../../../../components/Switch';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import MasterLookupService from '../../../../services/master-lookup.service';
import { Modules, AuthRoutes } from '../../../../enums';

interface CategoryFormData {
  category_code: string;
  category_name: string;
  display_order: number | string;
  status: 'active' | 'inactive';
  notes: string;
}

const initialForm: CategoryFormData = {
  category_code: '',
  category_name: '',
  display_order: 0,
  status: 'active',
  notes: '',
};

const CategoryForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<CategoryFormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    MasterLookupService.get('categories', id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          category_code: row.category_code ?? '',
          category_name: row.category_name ?? '',
          display_order: row.display_order ?? 0,
          status: row.status ?? 'active',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: CategoryFormData) => {
      const nextErrors: Record<string, string> = {};
      if (!data.category_code.trim()) nextErrors.category_code = t('masters.categories.form.pageTexts.fields.categoryCode.errors.required');
      if (!data.category_name.trim()) nextErrors.category_name = t('masters.categories.form.pageTexts.fields.categoryName.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'status' ? (e.target.checked ? 'active' : 'inactive') : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = { ...formData, display_order: Number(formData.display_order) || 0 };

        if (edit && id) {
          await MasterLookupService.update('categories', id, payload);
          toast.success(<Toast message={t('masters.categories.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await MasterLookupService.create('categories', payload);
          toast.success(<Toast message={t('masters.categories.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.categories}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.categories.form.pageTexts.editTitle') : t('masters.categories.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="category_code"
            name="category_code"
            label={t('masters.categories.form.pageTexts.fields.categoryCode.label')}
            placeholder={t('masters.categories.form.pageTexts.fields.categoryCode.placeholder')}
            required
            value={formData.category_code}
            error={!!errors.category_code}
            errorMessage={errors.category_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-5">
          <Input
            id="category_name"
            name="category_name"
            label={t('masters.categories.form.pageTexts.fields.categoryName.label')}
            placeholder={t('masters.categories.form.pageTexts.fields.categoryName.placeholder')}
            required
            value={formData.category_name}
            error={!!errors.category_name}
            errorMessage={errors.category_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2">
          <Input
            id="display_order"
            name="display_order"
            type="number"
            min="0"
            label={t('masters.categories.form.pageTexts.fields.displayOrder.label')}
            placeholder="0"
            value={formData.display_order}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-1">
          <CustomizedSwitch
            label={t('masters.categories.form.pageTexts.fields.status.label')}
            name="status"
            checked={formData.status === 'active'}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.categories.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.categories.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.categories.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.categories}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.categories.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;
